// src/pages/Settings/sections/Billing.jsx
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Section } from "../components/Section";
import { SubtleButton } from "../components/Buttons";
import "./Billing.css";
import { getUser } from "../../Login/authUtils";

const PROJECT_API = "https://rohanbackend.axiomos.in/projects";
// ⚠️ For production, move this to env & rotate it regularly
const BILLING_SECRET_KEY = "WRipPYbCXb";
const BILLING_PER_PROJECT = 18000; // 💰 fixed billing per approved project
const BILLING_APPROVER_ID = "68938029400b741f48f13c6c";

// 🔗 TODO: change this to your real upload endpoint
const BILLING_UPLOAD_API =
  "https://rohanbackend.axiomos.in/upload/billing-proof";

export default function Billing({ busy }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [fileUploadProjectId, setFileUploadProjectId] = useState(null);
  const [error, setError] = useState("");

  const fileInputRef = useRef(null);

  const user = getUser(); // ✅ { _id, userId, name, emailId, ... }
  const isBillingAdmin = user?.emailId === "axiomos2024@gmail.com";

  console.log("user from localStorage:", user);
  console.log("isBillingAdmin:", isBillingAdmin);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(PROJECT_API).catch(() => null);

      const allProjects = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.projects)
            ? res.data.projects
            : [];

      setProjects(allProjects);
    } catch (err) {
      console.error("Failed to load projects:", err);
      setError("Could not fetch projects. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    try {
      return amount.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      });
    } catch {
      return `₹${amount}`;
    }
  };

  const getApproverName = (approver) => {
    if (!approver) return "—";
    return (
      approver.name ||
      approver.fullName ||
      approver.emailId ||
      approver.email ||
      approver.userId ||
      approver._id ||
      "—"
    );
  };

  // 🔧 Icon helper for status
  const getStatusIconClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") return "fa-solid fa-circle-check";
    if (s === "rejected") return "fa-regular fa-circle-xmark";
    return "fa-regular fa-clock"; // pending / default
  };

  // 📊 Compute billing stats based on billingApprovalStatus
  const billingStats = (() => {
    const totalProjects = projects.length;
    let approved = 0;
    let pending = 0;
    let rejected = 0;

    projects.forEach((p) => {
      const status = (p.billingApprovalStatus || "pending").toLowerCase();
      if (status === "approved") approved += 1;
      else if (status === "rejected") rejected += 1;
      else pending += 1;
    });

    const approvedAmount = approved * BILLING_PER_PROJECT;
    const pendingAmount = pending * BILLING_PER_PROJECT;
    const rejectedAmount = rejected * BILLING_PER_PROJECT;
    const totalAmount = totalProjects * BILLING_PER_PROJECT;

    return {
      totalProjects,
      approved,
      pending,
      rejected,
      approvedAmount,
      pendingAmount,
      rejectedAmount,
      totalAmount,
    };
  })();

  const handleUpdateBillingStatus = async (project, newStatus) => {
    if (!isBillingAdmin) return;

    const actionLabel =
      newStatus === "approved"
        ? "Approve"
        : newStatus === "rejected"
          ? "Reject"
          : "Reset to Pending";

    const confirmMsg = `Are you sure you want to "${actionLabel}" billing for:\n\n${
      project.projectName
    } (${project.projectId || project._id})?`;
    if (!window.confirm(confirmMsg)) return;

    // Optional remarks prompt
    const billingApprovalRemarks = window.prompt(
      "Add optional remarks for this billing decision (or leave blank):",
      "",
    );

    setUpdatingId(project._id);
    setError("");

    try {
      await axios.put(
        `${PROJECT_API}/${project._id}/billing-approval/${BILLING_SECRET_KEY}`,
        {
          billingApprovalStatus: newStatus,
          // 🔒 always use fixed ObjectId for approver
          billingApprovedBy:
            newStatus === "pending" ? null : BILLING_APPROVER_ID,
          billingApprovalRemarks: billingApprovalRemarks || "",
        },
      );

      // 🔄 Refresh list to show updated status
      await loadProjects();
    } catch (err) {
      console.error("Failed to update billing approval:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to update billing approval. Please try again.",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  // ⬆️ Open file picker for a specific project
  const handleBillingProofClick = (projectId) => {
    if (!isBillingAdmin || busy) return;
    setFileUploadProjectId(projectId);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ⬆️ Handle actual file upload + project update (using /project/file)
  const handleBillingProofChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !fileUploadProjectId) return;

    setUploadingId(fileUploadProjectId);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", fileUploadProjectId);

      // 1️⃣ Upload file to your common file API
      const uploadRes = await axios.post(
        "https://rohanbackend.axiomos.in/projects/file",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // 2️⃣ Extract correct URL from response
      const billingPhotoUrl =
        uploadRes.data?.url ||
        uploadRes.data?.fileUrl ||
        uploadRes.data?.downloadUrl ||
        uploadRes.data?.path;

      if (!billingPhotoUrl) {
        throw new Error("Upload success but no file URL returned from server");
      }

      // 3️⃣ Update project record with the billing file URL
      await axios.put(`${PROJECT_API}/${fileUploadProjectId}`, {
        billingPhoto: billingPhotoUrl,
      });

      // 4️⃣ Refresh UI
      await loadProjects();
    } catch (err) {
      console.error("Failed to upload billing proof:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to upload billing proof. Please try again.",
      );
    } finally {
      setUploadingId(null);
      setFileUploadProjectId(null);
      if (event.target) event.target.value = "";
    }
  };

  const getRenewalDate = (approvedAt) => {
    if (!approvedAt) return "—";
    const d = new Date(approvedAt);
    if (Number.isNaN(d.getTime())) return "—";

    const nextYear = new Date(d);
    nextYear.setFullYear(d.getFullYear() + 1);

    return nextYear.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Section
      title="Billing & Project Approvals"
      description="Review all projects, control billing approval status, and track who approved what and when."
    >
      {/* hidden file input used for all rows */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={handleBillingProofChange}
      />

      <div className="billing-header-row">
        <div className="billing-header-left">
          <p className="billing-hint">
            Each <strong>approved</strong> project is billed at{" "}
            <strong>{formatCurrency(BILLING_PER_PROJECT)}</strong>. This view
            reads data from <code>Projects Module</code> at Home Page{" "}
          </p>
          {!isBillingAdmin && (
            <p className="billing-warning">
              <i
                className="fa-regular fa-eye-slash"
                aria-hidden="true"
                style={{ marginRight: 6 }}
              ></i>
              You have read-only access. Only the billing admin can approve or
              reject billing.
            </p>
          )}
        </div>
        <div className="billing-header-actions">
          <SubtleButton onClick={loadProjects} disabled={loading || busy}>
            <i
              className="fa-solid fa-rotate-right"
              aria-hidden="true"
              style={{ marginRight: 6 }}
            ></i>
            Refresh
          </SubtleButton>
        </div>
      </div>

      {/* 📊 Billing summary cards */}
      {projects.length > 0 && (
        <div className="billing-summary-grid">
          <div className="billing-card billing-card-approved">
            <div className="billing-card-label">Approved</div>
            <div className="billing-card-main">
              <span className="billing-card-icon">
                <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
              </span>
              <span className="billing-card-count">
                {billingStats.approved}
              </span>
              <span className="billing-card-pill">projects</span>
            </div>
            <div className="billing-card-amount">
              {formatCurrency(billingStats.approvedAmount)}
            </div>
          </div>

          <div className="billing-card billing-card-pending">
            <div className="billing-card-label">Pending Approval</div>
            <div className="billing-card-main">
              <span className="billing-card-icon">
                <i className="fa-regular fa-clock" aria-hidden="true"></i>
              </span>
              <span className="billing-card-count">{billingStats.pending}</span>
              <span className="billing-card-pill">projects</span>
            </div>
            <div className="billing-card-amount">
              {formatCurrency(billingStats.pendingAmount)}
            </div>
          </div>

          <div className="billing-card billing-card-rejected">
            <div className="billing-card-label">Rejected</div>
            <div className="billing-card-main">
              <span className="billing-card-icon">
                <i
                  className="fa-regular fa-circle-xmark"
                  aria-hidden="true"
                ></i>
              </span>
              <span className="billing-card-count">
                {billingStats.rejected}
              </span>
              <span className="billing-card-pill">projects</span>
            </div>
            <div className="billing-card-amount">
              {formatCurrency(billingStats.rejectedAmount)}
            </div>
          </div>

          <div className="billing-card billing-card-total">
            <div className="billing-card-label">Total (All Projects)</div>
            <div className="billing-card-main">
              <span className="billing-card-icon">
                <i className="fa-solid fa-file-invoice" aria-hidden="true"></i>
              </span>
              <span className="billing-card-count">
                {billingStats.totalProjects}
              </span>
              <span className="billing-card-pill">projects</span>
            </div>
            <div className="billing-card-amount">
              {formatCurrency(billingStats.totalAmount)}
            </div>
          </div>
        </div>
      )}

      {error && <div className="billing-error-banner">{error}</div>}

      {loading ? (
        <div className="billing-loading">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="billing-empty">No projects found.</div>
      ) : (
        <div className="billing-table-wrapper">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Project ID</th>
                <th>Project Name</th>
                <th>Site Location</th>
                <th>Project Status</th>
                <th>Billing Status</th>
                <th>Billing Amount</th>
                <th>Approved At</th>
                <th>Renewal Date</th>
                <th>Billing Proof</th>
                {isBillingAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => {
                const isRowUpdating = updatingId === project._id;
                const billingStatus =
                  project.billingApprovalStatus || "pending";

                const perProjectAmount =
                  billingStatus.toLowerCase() === "approved"
                    ? BILLING_PER_PROJECT
                    : 0;

                return (
                  <tr key={project._id}>
                    <td>{project.projectId || project._id}</td>
                    <td>{project.projectName}</td>
                    <td>{project.siteLocation}</td>
                    <td>{project.status}</td>

                    {/* Billing status with icon chip */}
                    <td>
                      <span
                        className={`billing-status-chip billing-status-${billingStatus}`}
                      >
                        <i
                          className={getStatusIconClass(billingStatus)}
                          aria-hidden="true"
                        ></i>
                        <span>
                          {billingStatus.charAt(0).toUpperCase() +
                            billingStatus.slice(1)}
                        </span>
                      </span>
                    </td>

                    {/* Billing Amount */}
                    <td>{formatCurrency(perProjectAmount)}</td>

                    {/* Approved At */}
                    <td>{formatDateTime(project.billingApprovedAt)}</td>

                    <td>{getRenewalDate(project.billingApprovedAt)}</td>

                    {/* Billing Photo (if any) + Upload */}
                    <td>
                      <div className="billing-proof-cell">
                        {project.billingPhoto ? (
                          /^https?:\/\//i.test(project.billingPhoto) ? (
                            <a
                              href={project.billingPhoto}
                              target="_blank"
                              rel="noreferrer"
                              className="billing-photo-link"
                            >
                              <i
                                className="fa-regular fa-file-lines"
                                aria-hidden="true"
                              ></i>
                              <span>View</span>
                            </a>
                          ) : (
                            <span className="billing-photo-text">
                              {project.billingPhoto}
                            </span>
                          )
                        ) : (
                          <span className="billing-photo-text">—</span>
                        )}

                        {isBillingAdmin && (
                          <button
                            type="button"
                            className="billing-proof-upload-btn"
                            disabled={
                              uploadingId === project._id ||
                              busy ||
                              isRowUpdating
                            }
                            onClick={() => handleBillingProofClick(project._id)}
                          >
                            <i
                              className="fa-solid fa-cloud-arrow-up"
                              aria-hidden="true"
                            ></i>
                            {uploadingId === project._id
                              ? "Uploading…"
                              : project.billingPhoto
                                ? "Change"
                                : "Upload"}
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    {isBillingAdmin && (
                      <td>
                        <div className="billing-action-buttons">
                          <SubtleButton
                            disabled={
                              isRowUpdating ||
                              busy ||
                              billingStatus === "approved"
                            }
                            onClick={() =>
                              handleUpdateBillingStatus(project, "approved")
                            }
                          >
                            <i
                              className="fa-solid fa-circle-check"
                              aria-hidden="true"
                              style={{ marginRight: 6 }}
                            ></i>
                          </SubtleButton>
                          <SubtleButton
                            disabled={
                              isRowUpdating ||
                              busy ||
                              billingStatus === "rejected"
                            }
                            onClick={() =>
                              handleUpdateBillingStatus(project, "rejected")
                            }
                          >
                            <i
                              className="fa-solid fa-circle-xmark"
                              aria-hidden="true"
                              style={{ marginRight: 6 }}
                            ></i>
                          </SubtleButton>
                          <SubtleButton
                            disabled={
                              isRowUpdating ||
                              busy ||
                              billingStatus === "pending"
                            }
                            onClick={() =>
                              handleUpdateBillingStatus(project, "pending")
                            }
                          >
                            <i
                              className="fa-solid fa-arrow-rotate-left"
                              aria-hidden="true"
                              style={{ marginRight: 6 }}
                            ></i>
                          </SubtleButton>
                        </div>
                        {isRowUpdating && (
                          <div className="billing-row-spinner">Updating…</div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
